import styled, { css } from "@rentiohq/web-theme";
import { RM_COLORS } from "../../rentmatch.colors";

export const Table = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  background: white;
  overflow-x: auto;
  overflow-y: visible;
`;

/* Shared grid: checkbox | candidate | rentmatch | activity | income | more-info | rating | actions */
/* The Rentmatch column needs to fit its "Rentmatch" header label plus the sort
   arrow — 80px was too narrow and let the arrow spill into the Activity column. */
const GRID_COLUMNS =
  "32px minmax(140px, 1.2fr) 110px minmax(130px, 0.9fr) 110px minmax(110px, 0.9fr) 100px 280px";

export const TableHeaderRow = styled.div`
  display: grid;
  grid-template-columns: ${GRID_COLUMNS};
  align-items: center;
  padding: 12px 0;
  margin: 0;
  border-bottom: 2px solid ${({ theme }) => theme.getColor("gray", 200)};
  background: white;
  min-width: fit-content;
`;

export const TableRow = styled.div<{ isDragging?: boolean }>`
  display: grid;
  grid-template-columns: ${GRID_COLUMNS};
  align-items: center;
  padding: 16px 0;
  border-bottom: 1px solid ${({ theme }) => theme.getColor("gray", 200)};
  background: white;
  transition: box-shadow 150ms ease;
  min-width: fit-content;
  cursor: ${({ isDragging }) =>
    isDragging ? "grabbing" : "pointer"} !important;

  ${({ isDragging }) =>
    isDragging &&
    css`
      background: white;
      box-shadow: 0 5px 15px rgba(6, 44, 82, 0.15);
      border-radius: 6px;
      opacity: 0.6;
      width: 600px !important;
      overflow: hidden;
    `}
`;

export const HeaderCell = styled.div<{ sortable?: boolean }>`
  font-size: 14px;
  font-weight: 600;
  text-transform: none;
  letter-spacing: 0;
  color: rgb(73, 73, 73);
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  white-space: nowrap;

  ${({ sortable }) =>
    sortable &&
    css`
      cursor: pointer;
      user-select: none;

      &:hover {
        color: ${({ theme }: { theme: any }) => theme.getColor("gray", 900)};
      }
    `}
`;

export const Cell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  overflow: hidden;
`;

export const CandidateNameContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  cursor: pointer;
`;

export const CandidateName = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.getColor("gray", 900)};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const CandidateMeta = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.getColor("gray", 500)};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const RentMatchBadge = styled.span<{
  bgColor?: string;
  textColor?: string;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 8px;
  border-radius: 9999px;
  font-size: 12px;
  font-weight: 600;
  background: ${({ theme, bgColor }) => bgColor || theme.getColor("gray", 100)};
  color: ${({ theme, textColor }) => textColor || theme.getColor("gray", 500)};
`;

// cspell:disable
export const IncomeText = styled.span<{ isInsufficient?: boolean }>`
  font-size: 13px;
  font-family: "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace;
  color: ${({ isInsufficient, theme }) =>
    isInsufficient ? RM_COLORS.red : theme.getColor("gray", 900)};
`;
// cspell:enable

export const InfoGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 13px;
  color: ${({ theme }) => theme.getColor("gray", 600)};
`;

export const InfoItem = styled.div`
  display: flex;
  align-items: center;
  gap: 3px;
`;

export const ActionsCell = styled.div`
  display: flex;
  align-items: stretch;
  justify-content: flex-end;
  height: 32px;
  gap: 0;
`;

export const SortIcon = styled.span<{ sorted?: boolean }>`
  display: inline-flex;
  color: ${({ theme, sorted }) =>
    sorted ? "inherit" : theme.getColor("gray", 400)};
`;

export const ActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  /* Use a minimum width instead of a fixed one so long action labels
     (e.g. the "remove no-show" button) are not clipped. */
  min-width: 120px;
  height: 100%;
  padding: 0 12px;
  box-sizing: border-box;
  line-height: 1;
  border: 1px solid ${RM_COLORS.gray300};
  border-radius: 0;
  background: white;
  color: ${RM_COLORS.gray700};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  position: relative;
  transition:
    background 120ms ease,
    border-color 120ms ease;

  /* First in group: round left corners */
  &:first-child {
    border-radius: 6px 0 0 6px;
  }

  /* Collapse shared border with previous button */
  & + & {
    margin-left: -1px;
  }

  &:hover {
    background: ${RM_COLORS.gray50};
    border-color: ${RM_COLORS.gray400};
    z-index: 1;
  }

  svg {
    flex-shrink: 0;
  }
`;

export const ChevronButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 100%;
  box-sizing: border-box;
  line-height: 1;
  border: 1px solid ${RM_COLORS.gray300};
  border-radius: 0 6px 6px 0;
  background: white;
  color: ${RM_COLORS.gray500};
  cursor: pointer;
  flex-shrink: 0;
  position: relative;
  transition: background 120ms ease;

  &:hover {
    background: ${RM_COLORS.gray50};
    z-index: 1;
  }
`;

export const DragPreviewPill = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.getColor("green", 100)};
  color: ${({ theme }) => theme.getColor("green", 800)};
  border: 1px solid ${({ theme }) => theme.getColor("green", 200)};
  border-radius: 20px;
  padding: 8px 16px;
  font-weight: 600;
  font-size: 14px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  white-space: nowrap;
`;

export const ChevronMenuWrapper = styled.div`
  display: flex;
  height: 100%;
  margin-left: -1px;
  z-index: 0;

  &:hover {
    z-index: 1;
  }
`;
